import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, InputAdornment, Link, Paper, Stack,
  TextField, Typography,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, duServeur, useMessageTraduit } from '../utils/messageTraduit';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MarqueOptimStock from '../components/commun/MarqueOptimStock';

/**
 * Page PUBLIQUE d'acceptation d'invitation — /invitation/:token
 *
 * Rendue AVANT la garde `!user` d'App.js : atteignable sans session, et aussi
 * par un utilisateur déjà connecté (il peut relayer le lien, ou gérer
 * plusieurs comptes).
 *
 * ⚠️ Le SÉLECTEUR DE LANGUE est monté ici, et c'est délibéré. Il ne vit
 * ailleurs que dans Header et SuperAdminShell — donc À L'INTÉRIEUR de l'app.
 * Or un invité n'y est jamais entré : sa langue vaut le défaut `'fr'`, câblé
 * dans LanguageContext, et il n'aurait aucun moyen d'en changer. Un invité
 * hébréophone recevrait une page en français, sans recours.
 */
/**
 * Habillage de la page — AU NIVEAU MODULE, et il doit y rester.
 *
 * 🔴 IL ÉTAIT DÉFINI À L'INTÉRIEUR D'`InvitationAccept`, ET C'ÉTAIT UN BOGUE DE
 * SAISIE. Un composant défini dans un autre est une NOUVELLE fonction à chaque
 * rendu ; React compare `element.type` par IDENTITÉ, voit un type différent, et
 * DÉMONTE puis REMONTE tout le sous-arbre. Comme ce cadre enveloppe le
 * formulaire entier, chaque frappe détruisait et recréait les cinq `<input>` :
 * le premier caractère passait, `autoFocus` ramenait le curseur sur « prénom »,
 * et les suivants y atterrissaient.
 *
 * ⚠️ CE DÉFAUT SE CACHAIT DERRIÈRE LA PREMIÈRE CHOSE QU'ON ESSAIE. Sur le champ
 * qui porte `autoFocus`, le curseur revient là où il était déjà : la saisie y
 * est parfaite. Mesuré — en tapant « ReouvenX » dans « prénom », les cinq nœuds
 * DOM sont bien remplacés à CHAQUE frappe, et pourtant les huit caractères
 * arrivent. Le défaut ne se manifeste qu'à partir du DEUXIÈME champ.
 *
 * ⚠️ Il n'a donc pas été introduit par le tunnel : il est là depuis le lot 5.
 * Le collage et les gestionnaires de mots de passe produisent UN seul événement,
 * donc UN seul remontage — la saisie assistée fonctionnait. Le tunnel force à
 * traverser plusieurs champs et plusieurs étapes au clavier, et l'a rendu
 * bloquant.
 *
 * ⚠️ Ne prend AUCUNE prop d'état : il n'a rien à recevoir du composant, ce qui
 * est précisément pourquoi le sortir ne coûte rien.
 */
const Cadre = ({ children }) => (
  <Box sx={{
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', bgcolor: 'background.default', p: 2,
  }}>
    <Box sx={{ maxWidth: 480, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <LanguageSwitcher />
      </Box>
      {/* 🔴 LA MARQUE, QUE CET ÉCRAN N'AVAIT PAS DU TOUT. Le tunnel est la porte
          par laquelle un client CRÉE SON ENTREPRISE — et c'était la seule
          surface de l'application à ne jamais se nommer. Le Login la portait
          trois fois, le tunnel zéro : deux portes qu'un prospect voit à quelques
          minutes d'intervalle, traitées à l'opposé. */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <MarqueOptimStock />
        <Box sx={{ mt: 2 }}>{children}</Box>
      </Paper>
    </Box>
  </Box>
);

/**
 * L'AVANCEMENT — trois segments, et le nom de l'étape en cours.
 *
 * 🔴 IL REMPLACE UN `Stepper` MUI, ET CE N'EST PAS UN CHANGEMENT DE GOÛT. Le
 * `Stepper` horizontal pose trois pastilles numérotées AVEC leur libellé côte à
 * côte : sur la largeur de 480 px de cette carte — et a fortiori sur un
 * téléphone — les trois libellés se chevauchaient ou se tronquaient. La barre
 * segmentée sépare les deux informations : *où j'en suis* est GRAPHIQUE, *ce que
 * je remplis* est ÉCRIT une seule fois, en toutes lettres, sans concurrence.
 *
 * ⚠️ LES TROIS CLÉS D'ÉTAPE RESTENT LUES. Passer à une barre nue les aurait
 * rendues orphelines, et le lot RU6 a tranché : trois clés jamais lues se
 * retirent. Ici on n'en retire aucune — on n'en affiche qu'UNE à la fois.
 */
const Avancement = ({ etape, total, nom }) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
      {Array.from({ length: total }, (_, i) => (
        <Box
          key={i}
          sx={{
            height: 4,
            flex: 1,
            borderRadius: 2,
            // ⚠️ Les segments FRANCHIS et le segment COURANT sont pleins : la
            // barre dit « voilà ce qui est derrière moi », pas « voilà ce que
            // j'ai validé ». Un segment courant vide ferait lire l'étape en
            // cours comme non commencée.
            bgcolor: i <= etape ? 'primary.main' : 'action.disabledBackground',
          }}
        />
      ))}
    </Stack>
    <Typography variant="body2" color="text.secondary">
      {`${etape + 1}/${total} · ${nom}`}
    </Typography>
  </Box>
);

function InvitationAccept({ token, user }) {
  const { t } = useLanguage();
  const navigate = useNavigate();

  // 'chargement' | 'formulaire' | 'refus'
  const [etat, setEtat] = useState('chargement');
  const [invitation, setInvitation] = useState(null);
  const [refus, setRefus] = useState(null);      // { raison, message, aide }

  const [form, setForm] = useState({ firstName: '', lastName: '', password: '', confirm: '' });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useMessageTraduit();

  /**
   * ── LE TUNNEL (lot 6f) ────────────────────────────────────────────────────
   * Trois étapes pour un ADMIN, UNE SEULE pour un USER.
   *
   * ⚠️ La distinction est STRUCTURELLE, pas cosmétique : un USER invité rejoint
   * une entreprise qui a DÉJÀ ses sites. Lui faire créer un stock lui donnerait
   * un pouvoir que son rôle n'a pas (`create_stock` est ADMIN-only) et le
   * rattacherait à un site que personne ne lui a assigné. Le serveur applique
   * la même règle : il ignore `stock` et `company` si le rôle n'est pas ADMIN.
   *
   * ⚠️ L'ORDRE EST compte → SITE → société, et l'étape facultative est la
   * DERNIÈRE. On ne place pas un « je remplirai plus tard » entre deux étapes
   * obligatoires : c'est la seule position où l'abandon ne coupe rien.
   */
  const [etape, setEtape] = useState(0);
  const [stockForm, setStockForm] = useState({
    name: '', address: '', phone: '', email: '', description: '',
  });
  const [societeForm, setSocieteForm] = useState({
    name: '', registration_number: '', billing_address: '', billing_country: '',
    representative_first_name: '', representative_last_name: '',
    representative_email: '', representative_phone: '',
  });

  const decrireRefus = useCallback((raison) => {
    switch (raison) {
      case 'expiree':
        return { raison, message: t('invitation.errorExpired'), aide: t('invitation.errorExpiredHint') };
      case 'revoquee':
        return { raison, message: t('invitation.errorRevoked'), aide: t('invitation.errorRevokedHint') };
      case 'acceptee':
        return { raison, message: t('invitation.errorAccepted'), aide: t('invitation.errorAcceptedHint') };
      case 'reseau':
        return { raison, message: t('invitation.errorNetwork'), aide: '' };
      default:
        // 404 muet : le jeton est inconnu, on n'en dit pas plus.
        return { raison: 'inconnu', message: t('invitation.errorUnknown'), aide: '' };
    }
  }, [t]);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const { data } = await apiService.get(`/auth/invitations/${token}`);
        if (annule) return;
        setInvitation(data);
        // ⚠️ PRÉ-REMPLISSAGE de la raison sociale, et MODIFIABLE. Le nom vient
        // du super-admin : c'est SON orthographe, pas celle du client. Sans ce
        // point d'entrée, l'invité verrait sa raison sociale mal écrite sur ses
        // bons de commande sans jamais savoir qu'il pouvait la corriger ici.
        setSocieteForm((s) => ({ ...s, name: data.company_name || '' }));
        setEtat('formulaire');
      } catch (e) {
        if (annule) return;
        // 404 = jeton inconnu (muet) · 410 = a existé, n'est plus utilisable
        // (les trois motifs sont distingués) · absence de réponse = réseau.
        const raison = e?.response?.data?.raison
          || (e?.response ? 'inconnu' : 'reseau');
        setRefus(decrireRefus(raison));
        setEtat('refus');
      }
    })();
    return () => { annule = true; };
  }, [token, decrireRefus]);

  const maj = (champ) => (ev) => setForm((f) => ({ ...f, [champ]: ev.target.value }));
  const majStock = (champ) => (ev) =>
    setStockForm((s) => ({ ...s, [champ]: ev.target.value }));
  const majSociete = (champ) => (ev) =>
    setSocieteForm((s) => ({ ...s, [champ]: ev.target.value }));

  const estAdmin = invitation?.role === 'ADMIN';
  const derniereEtape = estAdmin ? 2 : 0;
  const etapeSuivantePossible = etape < derniereEtape;

  /**
   * Contrôle des champs REQUIS de l'étape courante, avant de passer à la
   * suivante.
   *
   * ⚠️ Nécessaire parce que l'attribut `required` de HTML ne se déclenche qu'à
   * la SOUMISSION du formulaire : sans ce contrôle, « Suivant » laisserait
   * avancer avec une étape vide, et l'invité découvrirait le refus à la fin,
   * trois écrans plus loin, sans savoir lequel corriger.
   *
   * ⚠️ Il ne DUPLIQUE aucune règle serveur — il vérifie une PRÉSENCE, pas un
   * format. Le seul contrôle dont le front est réellement l'autorité reste la
   * confirmation du mot de passe, que le serveur ne reçoit pas.
   */
  /**
   * ⚠️ REND UN DESCRIPTEUR (`cle(...)`) OU `null`, PLUS UNE CHAÎNE TRADUITE.
   * Elle appelait `t()` au moment du contrôle — donc **avant** que le
   * dictionnaire soit chargé sur un premier rendu, ce qui gelait la clé brute
   * dans l'état. *Les deux appelants ne changent pas : un descripteur non-nul
   * est truthy, exactement comme une chaîne non vide l'était.*
   */
  const etapeIncomplete = () => {
    if (etape === 0) {
      if (!form.firstName.trim() || !form.lastName.trim() || !form.password) {
        return cle('invitation.requiredFields');
      }
      if (form.password !== form.confirm) return cle('invitation.passwordMismatch');
    }
    if (etape === 1) {
      if (!stockForm.name.trim() || !stockForm.address.trim() || !stockForm.phone.trim()) {
        return cle('invitation.siteRequired');
      }
    }
    return null;
  };

  const suivant = () => {
    const probleme = etapeIncomplete();
    if (probleme) { setErreur(probleme); return; }
    setErreur(null);
    setEtape(etape + 1);
  };

  /**
   * @param {boolean} avecSociete — `false` quand l'invité choisit « plus tard ».
   *
   * ⚠️ Le choix est passé en PARAMÈTRE, jamais par un `setState` suivi d'un
   * envoi : `setSocieteForm({})` puis soumettre enverrait l'ANCIEN état, React
   * n'appliquant la mise à jour qu'au rendu suivant. Le bogue aurait été
   * silencieux — la fiche serait partie alors que l'invité a cliqué
   * « plus tard ».
   */
  const envoyer = async (avecSociete) => {
    setErreur(null);

    // ⚠️ La CONFIRMATION est vérifiée ici et NULLE PART AILLEURS : le serveur
    // ne la reçoit pas et n'a pas à la connaître. C'est le seul contrôle dont
    // le front est l'autorité.
    if (form.password !== form.confirm) {
      setErreur(cle('invitation.passwordMismatch'));
      setEtape(0);
      return;
    }

    // ⚠️ La RÈGLE de mot de passe n'est PAS dupliquée ici. Son ÉNONCÉ est
    // affiché sous le champ pour que l'utilisateur ne la découvre pas après
    // avoir soumis, mais l'AUTORITÉ reste le serveur (utils/password_policy).
    setEnvoi(true);
    try {
      // ⚠️ CHAMP PAR CHAMP, jamais `{...societeForm}` étalé sans contrôle : la
      // route n'accepte que 8 clés et refuse les autres en 400. Ici les deux
      // objets sont déjà construits sur ces clés exactes, mais on n'envoie le
      // bloc `company` QUE s'il contient quelque chose — un objet de 8 chaînes
      // vides ferait écrire 8 NULL sur une fiche que le super-admin a peut-être
      // partiellement remplie.
      const societePleine = avecSociete
        ? Object.fromEntries(
          Object.entries(societeForm).filter(([, v]) => v.trim() !== ''),
        )
        : {};
      await apiService.post(`/auth/invitations/${token}/accept`, {
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        password: form.password,
        ...(estAdmin ? {
          stock: {
            name: stockForm.name.trim(),
            address: stockForm.address.trim(),
            phone: stockForm.phone.trim(),
            email: stockForm.email.trim(),
            description: stockForm.description.trim(),
          },
          ...(Object.keys(societePleine).length ? { company: societePleine } : {}),
        } : {}),
      });
      // Aucune session n'est ouverte : le message voyage par l'ÉTAT DE
      // NAVIGATION, pas par l'URL — un paramètre traînerait dans l'historique
      // et serait partageable.
      navigate('/', { replace: true, state: { message: t('invitation.created') } });
    } catch (e) {
      const raison = e?.response?.data?.raison;
      if (raison && ['expiree', 'revoquee', 'acceptee', 'deja_utilisee'].includes(raison)) {
        // L'invitation a changé d'état entre l'ouverture et l'envoi.
        setRefus(decrireRefus(raison === 'deja_utilisee' ? 'acceptee' : raison));
        setEtat('refus');
        return;
      }
      setErreur(duServeur(e, 'invitation.errorNetwork'));
    } finally {
      setEnvoi(false);
    }
  };

  /**
   * ── LE POINT D'ACTION UNIQUE DU PARCOURS ──────────────────────────────────
   * Avance d'une étape, ou envoie si c'est la dernière.
   *
   * 🔴 IL N'Y A PLUS AUCUN `type="submit"` DANS CE FORMULAIRE, ET C'EST LE
   * CORRECTIF. La version précédente choisissait entre deux boutons par une
   * ternaire au MÊME emplacement JSX :
   *     {suivantPossible ? <Button type="button" …/> : <Button type="submit" …/>}
   * React réconcilie en place — même composant, même position — et se contente
   * de MUTER l'attribut `type` du bouton EXISTANT. Mesuré par identité de nœud :
   * le `<button>` marqué à l'étape 1 est le MÊME à l'étape 3, avec `type` passé
   * de `button` à `submit`.
   * Conséquence au clic RÉEL : React vidange la mise à jour d'un événement
   * discret de façon SYNCHRONE, puis le navigateur exécute l'action par défaut
   * du bouton — qu'il relit APRÈS le rendu, donc `submit`. Le formulaire partait
   * au moment même où l'étape 3 s'affichait, et l'invité atterrissait sur la
   * page de connexion sans avoir jamais vu la fiche société.
   *
   * Un bouton permanent en `type="button"` supprime la CLASSE de défauts au
   * lieu de contourner celui-ci : sans action par défaut, il n'y a plus rien
   * que le navigateur puisse arbitrer dans notre dos. Une `key` distincte par
   * branche aurait aussi marché, mais elle aurait tenu à une ligne que
   * quelqu'un retirerait un jour en la croyant décorative.
   */
  const avancerOuEnvoyer = () => {
    if (etapeSuivantePossible) { suivant(); return; }
    const probleme = etapeIncomplete();
    if (probleme) { setErreur(probleme); return; }
    envoyer(true);
  };

  /**
   * Touche Entrée — MÊME point d'action que le bouton, jamais un autre.
   *
   * ⚠️ IL FAUT LE GÉRER EXPLICITEMENT. La « soumission implicite » du navigateur
   * (Entrée dans un champ → `submit`) exige qu'un bouton `type="submit"`
   * existe dans le formulaire. En les retirant tous, on a supprimé le défaut ET
   * la commodité : sans ce gestionnaire, Entrée ne ferait plus rien.
   *
   * ⚠️ EXCLUT `<textarea>` : « adresse de facturation » et « description » sont
   * multilignes, Entrée doit y insérer un retour à la ligne, pas valider
   * l'étape.
   */
  const surEntree = (ev) => {
    if (ev.key !== 'Enter' || ev.target.tagName === 'TEXTAREA') return;
    ev.preventDefault();
    avancerOuEnvoyer();
  };

  // Filet : sans bouton `submit`, cet événement ne devrait plus jamais se
  // produire. S'il survenait (extension, remplissage automatique), il appelle
  // LE MÊME point d'action — jamais une seconde logique parallèle.
  const soumettre = (ev) => {
    ev.preventDefault();
    avancerOuEnvoyer();
  };

  if (etat === 'chargement') {
    return (
      <Cadre>
        <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
          <CircularProgress />
          <Typography color="text.secondary">{t('invitation.checking')}</Typography>
        </Stack>
      </Cadre>
    );
  }

  if (etat === 'refus') {
    return (
      <Cadre>
        <Alert severity={refus.raison === 'acceptee' ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {refus.message}
          {refus.aide && <Box component="div" sx={{ mt: 0.5 }}>{refus.aide}</Box>}
        </Alert>
        {/* Sur « déjà accepté », le compte existe probablement : on propose la
            connexion. Sur « expiré », aucune action self-service n'existe — le
            renvoi est le lot 6, il faut passer par l'administrateur. */}
        {refus.raison === 'acceptee' && (
          <Button variant="contained" fullWidth onClick={() => navigate('/', { replace: true })}>
            {t('invitation.backToLogin')}
          </Button>
        )}
      </Cadre>
    );
  }

  return (
    <Cadre>
      {/* ⚠️ « Rejoindre OPTIMSTOCK » A ÉTÉ RETIRÉ DES DEUX BRANCHES, et c'est
          MON changement qui l'a rendu redondant : le bloc d'identité au-dessus
          dit déjà « OptimStock », dans la casse officielle du produit. Deux
          titres de même niveau, à trois lignes d'écart, dont l'un écrit le nom
          en capitales et l'autre pas. *Ajouter la marque oblige à retirer ce qui
          la répétait.* La clé `invitation.title` n'ayant plus aucun lecteur,
          elle sort des trois locales (doctrine RU6). */}
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('invitation.invitedAs')} <strong>{invitation.company_name}</strong>
      </Typography>

      {user && (
        /* Dit ce qui est VRAI : l'acceptation n'ouvre et ne touche aucune
           session, elle crée un compte SÉPARÉ. */
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('invitation.loggedInWarning', { email: user.email })}
        </Alert>
      )}

      {/* Le fil d'Ariane n'apparaît QUE pour un ADMIN : un USER n'a qu'une
          étape, et lui montrer « 1/1 » serait du bruit. */}
      {estAdmin && (
        <Avancement etape={etape} total={3}
                    nom={[t('invitation.stepAccount'), t('invitation.stepSite'),
                          t('invitation.stepCompany')][etape]} />
      )}

      <Box component="form" onSubmit={soumettre} onKeyDown={surEntree}>
        <Stack spacing={2}>
          {etape === 0 && (
            <>
              {/* ⚠️ LE CADENAS DIT CE QUE `readOnly` FAIT SANS LE DIRE. Un champ
                  en lecture seule ressemble à un champ ordinaire : on y clique,
                  on tape, rien ne se passe, et on croit à une panne. L'icône
                  donne la RAISON avant l'essai — l'aide sous le champ ne se lit
                  qu'après. */}
              <TextField
                label={t('invitation.emailLabel')}
                value={invitation.email}
                helperText={t('invitation.emailHelp')}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <LockOutlined color="disabled" fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
              <TextField label={t('invitation.firstName')} value={form.firstName}
                         onChange={maj('firstName')} required fullWidth autoFocus />
              <TextField label={t('invitation.lastName')} value={form.lastName}
                         onChange={maj('lastName')} required fullWidth />
              <TextField label={t('invitation.password')} type="password" value={form.password}
                         onChange={maj('password')} required fullWidth
                         helperText={t('invitation.passwordHint')} />
              <TextField label={t('invitation.passwordConfirm')} type="password"
                         value={form.confirm} onChange={maj('confirm')} required fullWidth
                         error={Boolean(form.confirm) && form.confirm !== form.password} />
            </>
          )}

          {etape === 1 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('invitation.siteHelp')}
              </Typography>
              {/* ⚠️ Nom, adresse et téléphone REQUIS — ils partent chez le
                  fournisseur (6d-i : le document porte les coordonnées du
                  SITE). Email et description facultatifs : même contrat que le
                  cul-de-sac de 6c, les deux écrans ne doivent pas diverger. */}
              <TextField label={t('invitation.siteName')} value={stockForm.name}
                         onChange={majStock('name')} required fullWidth autoFocus />
              <TextField label={t('invitation.siteAddress')} value={stockForm.address}
                         onChange={majStock('address')} required fullWidth />
              <TextField label={t('invitation.sitePhone')} value={stockForm.phone}
                         onChange={majStock('phone')} required fullWidth />
              <TextField label={t('invitation.siteEmail')} type="email"
                         value={stockForm.email} onChange={majStock('email')} fullWidth />
              <TextField label={t('invitation.siteDescription')} multiline rows={2}
                         value={stockForm.description}
                         onChange={majStock('description')} fullWidth />
            </>
          )}

          {etape === 2 && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('invitation.companyHelp')}
              </Typography>
              <TextField label={t('company.name')} value={societeForm.name}
                         onChange={majSociete('name')} fullWidth autoFocus
                         helperText={t('invitation.companyNameHelp')} />
              <TextField label={t('company.registration_number')}
                         value={societeForm.registration_number}
                         onChange={majSociete('registration_number')} fullWidth />
              <Box>
                <TextField label={t('company.billing_address')} multiline rows={2}
                           value={societeForm.billing_address}
                           onChange={majSociete('billing_address')} fullWidth />
                {/* ⚠️ RECOPIER, PAS LIER. L'adresse du site vient d'être saisie
                    à l'étape précédente et c'est TRÈS souvent la même — mais
                    « souvent » n'est pas « toujours » : un cabinet peut être
                    facturé au siège. Le lien remplit et se tait ; les deux
                    champs restent indépendants ensuite. Un rattachement
                    automatique ferait bouger la facturation le jour où le site
                    déménage.
                    ⚠️ Il ne s'affiche que si l'adresse du site existe ET
                    diffère : proposer de recopier ce qui est déjà là est du
                    bruit, et proposer de recopier du vide est un piège. */}
                {stockForm.address.trim()
                  && stockForm.address.trim() !== societeForm.billing_address.trim() && (
                  <Link component="button" type="button" variant="body2"
                        sx={{ mt: 0.5 }}
                        onClick={() => setSocieteForm((s) => ({
                          ...s, billing_address: stockForm.address.trim(),
                        }))}>
                    {t('invitation.reuseSiteAddress')}
                  </Link>
                )}
              </Box>
              {/* La contrainte est ÉNONCÉE, pas revalidée : le serveur reste
                  seul juge, et il normalise la casse. Même ligne que « Ma
                  société ». */}
              <TextField label={t('company.billing_country')}
                         value={societeForm.billing_country}
                         onChange={majSociete('billing_country')} fullWidth
                         helperText={t('company.billingCountryHint')} />
              <TextField label={t('company.representative_first_name')}
                         value={societeForm.representative_first_name}
                         onChange={majSociete('representative_first_name')} fullWidth />
              <TextField label={t('company.representative_last_name')}
                         value={societeForm.representative_last_name}
                         onChange={majSociete('representative_last_name')} fullWidth />
              <TextField label={t('company.representative_email')} type="email"
                         value={societeForm.representative_email}
                         onChange={majSociete('representative_email')} fullWidth />
              <TextField label={t('company.representative_phone')}
                         value={societeForm.representative_phone}
                         onChange={majSociete('representative_phone')} fullWidth />
            </>
          )}

          {erreur && <Alert severity="error">{erreur}</Alert>}

          <Stack direction="row" spacing={1}>
            {etape > 0 && (
              <Button onClick={() => { setErreur(null); setEtape(etape - 1); }}
                      disabled={envoi} fullWidth>
                {t('invitation.back')}
              </Button>
            )}
            {/* Le bouton de SOUMISSION n'existe qu'à la dernière étape du
                parcours — sur l'étape 2, « Terminer » et « Plus tard »
                envoient tous deux, la seule différence étant le bloc
                `company`. C'est ce qui rend l'étape 3 réellement facultative
                plutôt que « facultative mais il faut quand même cliquer ». */}
            {/* ⚠️ UN SEUL BOUTON, TOUJOURS `type="button"`. Seul le LIBELLÉ
                dépend de l'étape ; l'ACTION passe toujours par le même point.
                Ne pas réintroduire de ternaire entre deux <Button> de `type`
                différent : React réutiliserait le nœud et ferait basculer
                l'attribut sous le clic (cf. `avancerOuEnvoyer`). */}
            <Button type="button" variant="contained" size="large" fullWidth
                    disabled={envoi} onClick={avancerOuEnvoyer}>
              {etapeSuivantePossible
                ? t('invitation.next')
                : (envoi ? t('invitation.submitting') : t('invitation.submit'))}
            </Button>
          </Stack>

          {/* ⚠️ « Plus tard » est un `type="button"` qui appelle `envoyer(false)`
              DIRECTEMENT. La première version vidait `societeForm` puis
              soumettait : `setState` étant asynchrone, l'envoi serait parti
              avec l'ANCIEN état et la fiche aurait été écrite alors que
              l'invité venait de dire « plus tard ». Bogue silencieux, corrigé
              en passant le choix en paramètre plutôt que par l'état. */}
          {/* ⚠️ LA HIÉRARCHIE DIT QUE L'ÉTAPE EST FACULTATIVE. Un bouton PLEIN
              qui crée le compte, et DESSOUS un lien discret qui le crée aussi —
              sans la fiche. Deux boutons de même poids auraient présenté un
              choix là où il n'y en a pas : les deux chemins mènent au compte,
              seul le bloc `company` les sépare. */}
          {estAdmin && etape === 2 && (
            <Button type="button" variant="text" size="small" disabled={envoi}
                    sx={{ alignSelf: 'center' }}
                    onClick={() => envoyer(false)}>
              {envoi ? t('invitation.submitting') : t('invitation.companyLater')}
            </Button>
          )}
        </Stack>
      </Box>
    </Cadre>
  );
}

export default InvitationAccept;
